/* eslint-disable jsx-a11y/anchor-is-valid */
import { Drawer, Tabs, Typography, Switch, Spin, Carousel, Empty } from 'antd'
import styled from 'styled-components'
import { SignTitle } from '@/components/sign-title'
import { IconFont, SvgIcon, TagDetail } from '@/components'
import MarkdownEditor from '@/components/markdown-editor'
import DetailLog from './detail-log'
import { downloadPcap } from '@/api/threat-list-task'
import EmptyImg from '@/assets/imges/Empty/threat-protection-empty.png'
import ResponseEmpty from '@/assets/imges/Empty/response-empty.png'
import { useRef } from 'react'
import { t } from '@/i18n'
const { Paragraph } = Typography

const contentStyle: React.CSSProperties = {
  height: '160px',
  color: '#666',
  fontSize: '14px',
  padding: '16px',
  paddingLeft: '24px',
  background: '#f7f8fa',
  overflowY: 'scroll',
  borderRadius: '8px',
  width: '100%',
  border: '1px solid #D9D9D9',
  position: 'relative',
}

const ResourceLevelMap = {
  ResourceLevelJunior: t('初级'),
  ResourceLevelMiddle: t('中级'),
  ResourceLevelSenior: t('高级'),
}

const KillChainMap = {
  1: t('信息侦查'),
  2: t('武器构建'),
  3: t('载荷投递'),
  4: t('漏洞利用'),
  5: t('后门植入'),
  6: t('命令控制'),
  7: t('采取行动'),
}

export const DetailDrawer = props => {
  const { visible = true, onClose, dataList, logList = [], loading, active, id } = props

  const carouselReqRef = useRef<any>(null)
  const carouselResRef = useRef<any>(null)

  // 添加一个格式化HTTP响应的工具函数
  const formatHttpResponse = (response: string) => {
    const [headers, ...bodyParts] = response.split('\r\n\r\n')
    const body = bodyParts.join('\r\n\r\n')

    return {
      headers: headers.split('\r\n'),
      body: body,
    }
  }

  // 添加一个格式化PHP代码的工具函数
  const formatPHPCode = (code: string) =>
    code
      .replace(/\{/g, '{\n  ')
      .replace(/\}/g, '\n}')
      .replace(/;(?!\n)/g, ';\n')
      .replace(/\) +{/g, ') {')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line)
      .join('\n')

  // 拦截状态组件
  const InterceptStatus = () => {
    if (dataList?.intercept_status === 1) {
      return <div className="intercept">{t('拦截成功')}</div>
    }
    if (dataList?.intercept_status === 2) {
      return <div className="intercept-err">{t('防护失效')}</div>
    }
    if (dataList?.intercept_status === 3) {
      return (
        <div className="intercept-err" style={{ color: '#72b851', background: '#e9f5e3' }}>
          {t('检测成功')}
        </div>
      )
    }
    return null
  }

  // 危险等级组件
  const RiskLevel = () => {
    const riskMap = {
      1: t('低危'),
      2: t('中危'),
      3: t('高危'),
      4: t('严重'),
    }
    return <div>{riskMap[dataList?.risk_level] || '-'}</div>
  }

  // 参数配置组件
  const ParamsConfig = () => {
    if (!dataList?.params?.length) return null

    return (
      <div className="flex" style={{ flexDirection: 'column' }}>
        <div className="title" style={{ width: 100, marginBottom: 8 }}>
          {t('用例参数配置：')}
        </div>
        <div className="params">
          {dataList.params.map((item, paramIndex) => (
            <div className="params-flex" key={paramIndex}>
              <Typography.Text className="params-title" ellipsis={{ tooltip: true, suffix: ':' }}>
                {item?.key}
              </Typography.Text>
              <div className="content">
                {item.type === 'bool' ? (
                  <Switch style={{ marginBottom: 8 }} checked={item.value === 'true'} disabled />
                ) : item.type === 'password' ? (
                  <div style={{ marginBottom: 8 }}>●●●●●●●●</div>
                ) : (
                  <div style={{ marginBottom: 8 }}>
                    {item.value && item.type === 'file' && <IconFont type={'icon-fujian2'} />}
                    {item.value || '-'}
                  </div>
                )}
                <Paragraph
                  className="describe"
                  style={{
                    marginBottom: paramIndex + 1 === dataList?.params?.length ? 0 : undefined,
                  }}
                >
                  {item.description}
                </Paragraph>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ATT&CK 组件
  const AttackTactics = () => {
    if (!dataList?.tactic_list?.length) return null

    return (
      <div className="flex" style={{ marginBottom: 0 }}>
        <div className="title">ATT&CK：</div>
        <div style={{ width: 'calc(100% - 112px)' }}>
          {dataList.tactic_list.map((item, tacticIndex) => (
            <div className="att-text" key={tacticIndex}>
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                {item?.name}
              </a>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const BasicInfo = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <>
          <div className="flex">
            <p className="title">{t('用例名称：')}</p>
            <Typography.Text ellipsis={{ tooltip: true }}>{dataList?.plugin_name}</Typography.Text>
          </div>
          <div className="flex">
            <p className="title">{t('验证模式：')}</p>
            <div>Agent → Agent</div>
          </div>
          <div className="flex">
            <p className="title">{t('拦截状态：')}</p>
            <InterceptStatus />
            {dataList?.intercept_from === 'interception_from_manual_correct' && (
              <span
                style={{
                  marginLeft: 5,
                  borderRadius: 6,
                  padding: '0 2px',
                  fontSize: 12,
                  color: '#1bbc40',
                  background: '#d9f4df',
                  height: 20,
                  lineHeight: '20px',
                }}
              >
                {t('已修正')}
              </span>
            )}
          </div>
          <div className="flex">
            <p className="title">{t('危险等级：')}</p>
            <RiskLevel />
          </div>
          {dataList?.resource_tag_list && dataList?.resource_tag_list?.length > 0 && (
            <TagDetail resourceTagList={dataList?.resource_tag_list || []} />
          )}
          <div className="flex">
            <p className="title">{t('攻击类型：')}</p>
            <div>{dataList?.attack_type_name || '-'}</div>
          </div>
          <div className="flex">
            <p className="title">{t('用例分级：')}</p>
            <div>{ResourceLevelMap[dataList?.resource_level] || '-'}</div>
          </div>
          <div className="flex">
            <p className="title">{t('杀伤链：')}</p>
            <div>{KillChainMap[dataList?.kill_chain] || '-'}</div>
          </div>
          <div className="flex">
            <p className="title">{t('模板名称：')}</p>
            <div>{dataList?.template_names?.map(item => item).join(', ') || '-'}</div>
          </div>
          <div className="flex">
            <p className="title">{t('攻击组织：')}</p>
            <div>{dataList?.apt_name || '-'}</div>
          </div>
          <div className="flex">
            <p className="title">{t('影响组件：')}</p>
            <div>{dataList?.component_name || '-'}</div>
          </div>
          <ParamsConfig />
          <div className="flex" style={{ alignItems: 'center' }}>
            <p className="title">{t('更新时间：')}</p>
            <div>{dataList?.update_time}</div>
          </div>
          <AttackTactics />
          {dataList?.describe && (
            <div className="block">
              <p className="title">{t('用例描述：')}</p>
              <MarkdownEditor
                isEdit={false}
                onlyDisplay
                setValue={() => {}}
                value={dataList?.describe}
              />
            </div>
          )}
          {dataList?.solution && (
            <div className="block">
              <p className="title">{t('解决方案：')}</p>
              <MarkdownEditor
                isEdit={false}
                onlyDisplay
                setValue={() => {}}
                value={dataList?.solution}
              />
            </div>
          )}
        </>
      )}
    </DetailDrawerBox>
  )

  const AttackDetail = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <>
          <div className="flex">
            <span style={{ display: 'flex', alignItems: 'center', width: '18%' }}>
              <SvgIcon name={'攻击源1'} style={{ width: '15px', height: '15px' }} />
              <span style={{ fontWeight: '500', marginLeft: 5 }}>{t('攻击源')}</span>
            </span>

            <span style={{ color: '#558492', display: 'flex', alignItems: 'center', width: '32%' }}>
              <SvgIcon name={'箭头'} style={{ width: '135px', height: '15px' }} />
            </span>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <SvgIcon name={'攻击目标'} style={{ width: '15px', height: '15px' }} />
              <span style={{ fontWeight: '500', marginLeft: 5 }}>{t('攻击目标')}</span>
            </span>
          </div>
          <div className="flex-two">
            <div className="flex-name">
              <p className="title" style={{ width: 25, marginRight: '0' }}>
                IP：
              </p>
              {/* 源IP*/}
              <div className="name1">{dataList?.attack_ip || '-'}</div>
            </div>
            <div className="flex-name">
              <p className="title" style={{ width: 25, marginRight: '0' }}>
                IP：
              </p>
              {/* 目的IP*/}
              <div>{dataList?.defense_ip || '-'}</div>
            </div>
          </div>
          <div className="flex-two">
            <div className="flex-name">
              <p className="title" style={{ width: 40, marginRight: '0' }}>
                {t('端口：')}
              </p>
              {/* 源端口*/}
              {dataList?.attack_port || '-'}
            </div>
            <div className="flex-name">
              <p className="title" style={{ width: 40, marginRight: '0' }}>
                {t('端口：')}
              </p>
              {/* 目的端口*/}
              {dataList?.defense_port || '-'}
            </div>
          </div>
          <div className="flex" style={{ alignItems: 'center' }}>
            <p className="title" style={{ width: 100 }}>
              {t('执行开始时间：')}
            </p>
            <div className="text" style={{ width: 'calc(100% - 100px' }}>
              {dataList?.start_time}
            </div>
          </div>
          <div className="flex" style={{ alignItems: 'center' }}>
            <p className="title" style={{ width: 100 }}>
              {t('执行结束时间：')}
            </p>
            <div className="text" style={{ width: 'calc(100% - 100px' }}>
              {dataList?.end_time}
            </div>
          </div>
          {dataList?.req_and_resp_list?.length > 0 ? (
            <>
              <Deatail>
                <div className="attckRequest">
                  <p
                    style={{
                      color: '#86909C',
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '95%',
                    }}
                  >
                    <p className="title">
                      <span>{t('攻击请求')}</span>
                      <span>：</span>
                    </p>
                    {dataList?.has_pcap_file && (
                      <a
                        rel="noopener noreferrer"
                        target="_blank"
                        onClick={() => {
                          downloadPcap({ resultId: id, resultPluginId: active }).catch(err => {
                            console.log(err)
                            const blob = window.URL.createObjectURL(new Blob([err]))
                            const aLink = document.createElement('a')
                            aLink.style.display = 'none'
                            aLink.href = blob
                            aLink.download = `${dataList?.plugin_name}.zip`
                            document.body?.appendChild(aLink)
                            aLink.click()
                            document.body?.removeChild(aLink)
                          })
                        }}
                      >
                        <IconFont
                          style={{ color: '#4238C9', marginRight: '4px' }}
                          type="icon-xiazai-wenjianxiazai-16"
                        />

                        <span style={{ color: '#4238C9' }}>{t('PCAP下载')}</span>
                      </a>
                    )}
                  </p>
                  <p>
                    <p className="title">
                      <span>{t('协议')}</span>
                      <span>：</span>
                    </p>
                    <span>{dataList?.protocol}</span>
                  </p>
                  <div className="block carousel-box">
                    <p
                      className="title"
                      style={{
                        width: '100%',
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingRight: '30px',
                      }}
                    >
                      <span style={{ lineHeight: '24px' }}>{t('HTTP请求：')}</span>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        {dataList?.req_and_resp_list?.length > 1 && (
                          <div className="prev">
                            <IconFont
                              type="icon-fanhui2"
                              onClick={() => {
                                if (carouselResRef.current) {
                                  carouselReqRef.current.prev()
                                  carouselResRef.current.prev()
                                }
                              }}
                            />
                          </div>
                        )}
                        {dataList?.req_and_resp_list?.length > 1 && (
                          <div className="next">
                            <IconFont
                              style={{ transform: 'rotateY(180deg)' }}
                              type="icon-fanhui2"
                              onClick={() => {
                                if (carouselResRef.current) {
                                  carouselReqRef.current.next()
                                  carouselResRef.current.next()
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </p>

                    {dataList?.req_and_resp_list?.length > 0 ? (
                      <Carousel dots={false} ref={carouselReqRef}>
                        {dataList?.req_and_resp_list?.map((item, index) => (
                          <div key={index}>
                            <div style={contentStyle}>
                              {dataList?.req_and_resp_list?.length > 1 && (
                                <IconFont
                                  type="icon-fanhui2"
                                  style={{
                                    fontSize: '12px',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '2px',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    if (carouselReqRef.current) {
                                      carouselReqRef.current.prev()
                                      carouselResRef.current.prev()
                                    }
                                  }}
                                />
                              )}
                              {item.request
                                .split('\r\n')
                                .filter(e => e !== '')
                                .map((v, i) => {
                                  if (i === 0) return v
                                  else {
                                    if (v.indexOf(':') !== -1)
                                      return { key: v.split(':')[0], value: v.split(':')[1] }
                                    else return { key: 'body', value: v }
                                  }
                                })
                                .map((it, ind) => {
                                  if (typeof it !== 'object') {
                                    return (
                                      <span
                                        key={ind}
                                        style={{
                                          display: 'inline-block',
                                          marginBottom: '4px',
                                          color: '#558490',
                                        }}
                                      >
                                        {it}
                                      </span>
                                    )
                                  } else {
                                    return (
                                      <div
                                        key={ind}
                                        style={{ display: 'flex', marginBottom: '4px' }}
                                      >
                                        <span
                                          style={{
                                            marginRight: '4px',
                                            color: '#558490',
                                            flexShrink: 0,
                                          }}
                                        >
                                          {it.key}：
                                        </span>
                                        <span>{it.value}</span>
                                      </div>
                                    )
                                  }
                                })}
                            </div>
                          </div>
                        ))}
                      </Carousel>
                    ) : (
                      <Empty image={EmptyImg} />
                    )}
                  </div>

                  <div className="block carousel-box">
                    <p
                      className="title"
                      style={{
                        width: '100%',
                        marginBottom: '20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingRight: '30px',
                      }}
                    >
                      <span style={{ lineHeight: '24px' }}>{t('HTTP响应：')}</span>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        {dataList?.req_and_resp_list?.length > 1 && (
                          <div className="prev">
                            <IconFont
                              type="icon-fanhui2"
                              onClick={() => {
                                if (carouselResRef.current) {
                                  carouselReqRef.current.prev()
                                  carouselResRef.current.prev()
                                }
                              }}
                            />
                          </div>
                        )}
                        {dataList?.req_and_resp_list?.length > 1 && (
                          <div className="next">
                            <IconFont
                              style={{ transform: 'rotateY(180deg)' }}
                              type="icon-fanhui2"
                              onClick={() => {
                                if (carouselResRef.current) {
                                  carouselReqRef.current.next()
                                  carouselResRef.current.next()
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </p>
                    {dataList?.req_and_resp_list?.length > 0 ? (
                      <Carousel dots={false} ref={carouselResRef}>
                        {dataList?.req_and_resp_list?.map((item, index) => (
                          <div key={index}>
                            <div style={contentStyle}>
                              {dataList?.req_and_resp_list?.length > 1 && (
                                <IconFont
                                  type="icon-fanhui2"
                                  style={{
                                    fontSize: '12px',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '2px',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    if (carouselResRef.current) {
                                      carouselReqRef.current.prev()
                                      carouselResRef.current.prev()
                                    }
                                  }}
                                />
                              )}
                              {item.response ? (
                                <div>
                                  {/* 响应头部分 */}
                                  {formatHttpResponse(item.response).headers.map(
                                    (header, headerIndex) => {
                                      if (headerIndex === 0) {
                                        // 状态行特殊处理
                                        return (
                                          <div
                                            key={headerIndex}
                                            style={{ marginBottom: '8px', color: '#558490' }}
                                          >
                                            {header}
                                          </div>
                                        )
                                      }

                                      const [key, ...values] = header.split(':')
                                      const value = values.join(':')

                                      return (
                                        <div
                                          key={headerIndex}
                                          style={{ display: 'flex', marginBottom: '4px' }}
                                        >
                                          <span
                                            style={{
                                              color: '#558490',
                                              marginRight: '4px',
                                              flexShrink: 0,
                                              fontWeight: 500,
                                            }}
                                          >
                                            {key}:
                                          </span>
                                          <span style={{ color: '#333' }}>{value}</span>
                                        </div>
                                      )
                                    },
                                  )}

                                  {/* 响应体部分 */}
                                  <pre
                                    style={{
                                      background: '#f5f5f5',
                                      padding: '12px',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      lineHeight: '1.5',
                                      overflow: 'auto',
                                      whiteSpace: 'pre-wrap',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {formatPHPCode(formatHttpResponse(item.response).body)}
                                  </pre>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Empty
                                    image={ResponseEmpty}
                                    description={t('暂无数据')}
                                    imageStyle={{ height: 100 }}
                                    style={{ margin: '200px 0' }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </Carousel>
                    ) : (
                      <Empty image={EmptyImg} />
                    )}
                  </div>
                </div>
              </Deatail>
            </>
          ) : (
            dataList?.has_pcap_file && (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: '0 20px',
                  marginBottom: '10px',
                }}
              >
                <a
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={() => {
                    downloadPcap({ resultId: id, resultPluginId: active }).catch(err => {
                      const blob = window.URL.createObjectURL(new Blob([err]))
                      const aLink = document.createElement('a')
                      aLink.style.display = 'none'
                      aLink.href = blob
                      aLink.download = `${dataList?.plugin_name}.zip`
                      document.body?.appendChild(aLink)
                      aLink.click()
                      document.body?.removeChild(aLink)
                    })
                  }}
                >
                  <IconFont
                    style={{ color: '#4238C9', marginRight: '4px' }}
                    type="icon-xiazai-wenjianxiazai-16"
                  />

                  <span style={{ color: '#4238C9' }}>{t('PCAP下载')}</span>
                </a>
              </div>
            )
          )}
          {dataList?.plugin_log && (
            <div className="flex" style={{ flexDirection: 'column' }}>
              <p className="title" style={{ width: 100 }}>
                {t('执行日志：')}
              </p>
              <div className="content" style={{ width: '100%' }}>
                {dataList?.plugin_log?.split('\n')?.map((item, i) => (
                  <div key={i}>{item}</div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </DetailDrawerBox>
  )

  const LogDetail = () => (
    <DetailDrawerBox>
      {loading ? (
        <Spin />
      ) : (
        <div className="log-alarm">
          <div className="log-alarm-title">{t('告警日志：')}</div>
          {logList?.map((item, index) => (
            <DetailLog index={index} data={item} key={index} dataList={dataList} />
          ))}
        </div>
      )}
    </DetailDrawerBox>
  )

  return (
    <DetailDrawerContainer>
      <Drawer
        title={<SignTitle show>{t('用例详情')}</SignTitle>}
        placement="right"
        closable
        onClose={onClose}
        open={visible}
        width={600}
        destroyOnClose
      >
        <Tabs
          defaultActiveKey="1"
          items={
            logList.length > 0
              ? [
                  { label: t('用例信息'), key: '1', children: <BasicInfo /> },
                  { label: t('攻击详情'), key: '2', children: <AttackDetail /> },
                  { label: t('告警日志'), key: '3', children: <LogDetail /> },
                ]
              : [
                  { label: t('用例信息'), key: '1', children: <BasicInfo /> },
                  { label: t('攻击详情'), key: '2', children: <AttackDetail /> },
                ]
          }
        />
      </Drawer>
    </DetailDrawerContainer>
  )
}

const DetailDrawerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const DetailDrawerBox = styled.div`
  .log-alarm-title {
    margin-bottom: 12px;
  }

  .block {
    margin: 12px 0;
    .title {
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
    }
  }
  .intercept,
  .intercept-err {
    width: 64px;
    height: 20px;
    background: #e5eeff;
    border-radius: 4px 12px 4px 12px;
    font-size: 12px;
    color: #2973ff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .intercept-err {
    color: #f44f4f;
    background: #fde5e5;
  }
  .content {
    word-break: break-all;
    color: #666666;
    border-radius: 4px;
    .headers {
      display: flex;
      margin-bottom: 5px;
    }
    .marked {
      white-space: nowrap;
      color: #558490;
      margin-right: 5px;
    }
    .marked-body {
      color: #558490;
    }
  }
  .header {
    // max-height: 200px;
    overflow-y: auto;
  }
  .flex,
  .flex-name {
    display: flex;
    margin-bottom: 15px;
    .content {
      color: #666666;
      border-radius: 4px;
      padding: 0 10px;
      width: calc(98% - 128px);
      background: #f7f8fa;
    }
    .title {
      height: 14px;
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
      margin-bottom: 0px;
    }
    .name {
      flex: 1;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
  .flex-name {
    width: 50%;
  }
  .flex-two {
    display: flex;
    .title {
      height: 14px;
      font-size: 14px;
      color: #86909c;
      line-height: 16px;
      width: 92px;
      text-align: left;
      margin-right: 18px;
      white-space: nowrap;
    }

    .name {
      width: 55%;
    }
  }
  .params {
    width: 100%;
    background: #f8f9fb;
    border-radius: 6px;
    padding: 12px;
    margin-top: 8px;
    .sample {
      border-top: 1px dashed #d5d9dd;
      padding: 10px;
      &:first-child {
        border: 0;
      }
    }
  }
  .params-flex {
    display: flex;
  }
  .params-title {
    height: 20px;
    width: 106px;
  }
`

const Deatail = styled.div`
  .attckRequest {
    .title {
      width: 86px;
    }
  }
  .carousel-box {
    .ant-carousel {
      .anticon {
        display: none;
      }
    }
  }
  .prev {
    font-size: 12px;
    margin: 0;
    padding: 0;
    color: #838383;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    margin-right: 12px;

    &:hover {
      border: 1px solid #4238c9;
      color: #4238c9;
      cursor: pointer;
    }
  }

  .next {
    font-size: 12px;
    margin: 0;
    padding: 0;
    color: #838383;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);
    border-radius: 6px;
    border: 1px solid #d9d9d9;

    &:hover {
      border: 1px solid #4238c9;
      color: #4238c9;
      cursor: pointer;
    }
  }

  .title {
    margin: 0;
    padding: 0;
    display: inline-block;
  }
  .content {
    color: #666666;
    border-radius: 4px;
    line-height: 1.2;
    padding: 10px;
    width: calc(98% - 18px);
    background: #f7f8fa;
    height: 170px;
    margin-bottom: 16px;
  }
  .arrowTotal {
    display: flex;
    gap: 8px;
    padding-right: 27px;
    .arrow {
      margin: 0;
      padding: 0;
      font-size: 16px;
      color: #838383;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      box-shadow: 0px 2px 4px 0px rgba(95, 103, 129, 0.06);
      border-radius: 6px;
      border: 1px solid #d9d9d9;
      &:hover {
        border: 1px solid #4238c9;
        color: #4238c9;
      }
    }
  }
  .equipmentIPs {
    .icon {
      color: #fff;
      display: inline-block;
      padding: 0 2px;
      background: #558492;
      border-radius: 4px;
      margin-right: 2px;
    }
    .yes {
      background: #f44f4f;
      border-radius: 4px;
      color: #fff;
      padding: 1px 2px;
      margin-left: 8px;
    }
    .no {
      background: #3586ff;
      border-radius: 4px;
      color: #fff;
      padding: 1px 2px;
      margin-left: 8px;
    }
    .logTotal {
      margin: 0;
      background: #e5e8ed;
      box-shadow: 0px 4px 6px 0px rgba(95, 103, 129, 0.06);
      border-radius: 6px;
      padding: 6px 0;
    }
    .log {
      cursor: pointer;
      border-radius: 6px;
      padding: 6px 8px;
    }
    .logHover {
      cursor: pointer;
      border-radius: 6px;
      padding: 6px 8px;
      background: #4238c9;
      color: #fff;
    }
  }
`
